import { useEffect, useRef, useState } from 'react';
import { Auth } from '@leagueplatform/auth';
import { captureError } from '@leagueplatform/observability';
import {
  onSalesforceEvent,
  SALESFORCE_EVENTS,
  clearSalesforceMessagingSession,
  setSalesforceIdentityToken,
  setSalesforceChatTokenProvider,
  setSalesforceChatBlocked,
} from '../embedded-chat';
import { getSalesforceChatToken } from '../api/get-salesforce-chat-token';

/**
 * Status of the Salesforce User Verification process.
 *
 * - `'idle'`                — verification is inactive (the hook is disabled or has not
 *                             yet started).
 * - `'pending'`             — verification is in progress (waiting for Salesforce
 *                             Messaging and/or Auth to become ready).
 * - `'verified'`            — an identity token was successfully sent to Salesforce.
 * - `'failed'`              — the identity token could not be obtained or sent.
 * - `'session-clear-failed'` — the Salesforce session could not be cleared before
 *                             verification. The widget has been hidden to prevent
 *                             cross-user session reuse. Use this status to show the
 *                             user an error (e.g. "chat is temporarily unavailable").
 */
export type SalesforceUserVerificationStatus =
  | 'idle'
  | 'pending'
  | 'verified'
  | 'failed'
  | 'session-clear-failed';

export interface SalesforceUserVerificationOptions {
  /**
   * Optional identity token provider that bypasses `Auth.getToken()` and the
   * League Identity token exchange. When supplied, the resolved token is sent
   * directly to Salesforce as the identity token, and the hook will NOT
   * subscribe to `Auth.addOnAuthListener`; the provider is assumed ready when
   * it resolves with a token string.
   *
   * Intended for host apps that manage their own identity token lifecycle
   * (e.g. B2B's cookie-to-JWT bridge) without initializing `@leagueplatform/auth`.
   * When omitted, `Auth.getToken()` is exchanged for a limited-scope Salesforce
   * chat JWT via `POST /v1/salesforce-chat-token`.
   *
   * **Note:** This option must be provided (or omitted) for the entire lifetime
   * of the hook. Switching between `undefined` and a function while `enabled`
   * is `true` is not supported and will not re-initialize the auth strategy.
   */
  getToken?: () => Promise<string>;
}

export interface SalesforceUserVerificationResult {
  /** Current status of the user verification flow. */
  verificationStatus: SalesforceUserVerificationStatus;
}

/**
 * React hook to wire up Salesforce User Verification.
 *
 * Listens for `onEmbeddedMessagingReady` and sends an identity token, then
 * listens for `onEmbeddedMessagingIdentityTokenExpired` and refreshes the token.
 *
 * @param enabled - When `false`, verification is disabled and `verificationStatus` is `'idle'`.
 * @param options.getToken - Optional override. When supplied, bypasses `Auth.getToken()` and uses
 *   this provider instead. Intended for host apps that manage their own JWT lifecycle (e.g. a
 *   cookie-to-JWT bridge) without initializing `@leagueplatform/auth`. When provided, the hook
 *   does not subscribe to `Auth.addOnAuthListener`.
 * @returns `{ verificationStatus }` - The current state of the verification flow:
 *   - `'idle'` — disabled or not yet started
 *   - `'pending'` — waiting for Salesforce Messaging and/or Auth to be ready
 *   - `'verified'` — identity token successfully sent
 *   - `'failed'` — token could not be obtained or sent
 *   - `'session-clear-failed'` — the prior session could not be cleared; chat has been
 *     hidden to prevent cross-user session exposure. Show the user an error (e.g. a toast).
 *
 * @see https://developer.salesforce.com/docs/service/messaging-web/guide/user-verification.html
 */
export const useSalesforceUserVerification = (
  enabled = true,
  options?: SalesforceUserVerificationOptions,
): SalesforceUserVerificationResult => {
  const tokenProviderRef = useRef(options?.getToken);
  tokenProviderRef.current = options?.getToken;

  const [verificationStatus, setVerificationStatus] =
    useState<SalesforceUserVerificationStatus>('idle');

  useEffect(() => {
    if (!enabled) {
      setVerificationStatus('idle');
      setSalesforceChatTokenProvider(undefined);
      // No-op cleanup when verification is disabled, to satisfy hooks/ESLint expectations
      return () => {};
    }

    setVerificationStatus('pending');

    // Register a stable wrapper for module-level REST API calls (e.g. closeSalesforceConversation)
    // so they use the same token source as identity verification. The wrapper reads from the ref
    // on each invocation so it always reflects the latest provider even if the function reference
    // changes across renders without triggering this effect. Passing undefined reverts to the
    // default embedded-chat behavior of calling getSalesforceChatToken(), which exchanges
    // Auth.getToken() via the Identity endpoint.
    const tokenProviderWrapper = tokenProviderRef.current
      ? () => tokenProviderRef.current!()
      : undefined;
    setSalesforceChatTokenProvider(tokenProviderWrapper);

    // Cancellation flag for async work — set to `false` in cleanup to prevent
    // stale `resolveToken()` promises from calling `setSalesforceIdentityToken`
    // after this effect has been invalidated.
    let isMounted = true;
    // We need a local flag for this hook in addition to window.leagueSalesforceMessagingReady
    // because the hook may be mounted before the Messaging script is loaded (e.g., if integrators
    // use this hook and do not initialize Salesforce beforehand). In this file, it serves as the
    // single source of truth for whether Messaging is ready *for this hook instance*.
    let messagingReadyForHook = false;
    let hasAuthReady = false;
    let hasAttemptedSessionClear = false;
    let hasSucceededSessionClear = false;

    const sendIdentityToken = async (sendOptions?: {
      /**
       * When true, clears any existing Messaging session before setting the identity
       * token. Intended for the initial verification on hook mount, NOT for token
       * refresh on `onEmbeddedMessagingIdentityTokenExpired`.
       */
      clearExistingSession?: boolean;
    }) => {
      try {
        // Always clear the Salesforce session on the initial identity token send
        // to prevent cross-user session reuse (e.g. shared device, tab close without logout).
        // `hasAttemptedSessionClear` is set before the await to prevent infinite loops:
        // a successful clearSession causes Salesforce to re-fire READY, and without this
        // guard we would clear repeatedly. `hasSucceededSessionClear` is set after the
        // await; if the clear failed (threw), we skip setIdentityToken on any subsequent
        // READY re-fires rather than proceeding with an un-cleared session.
        if (sendOptions?.clearExistingSession) {
          if (!hasAttemptedSessionClear) {
            hasAttemptedSessionClear = true;
            await clearSalesforceMessagingSession(true);
            hasSucceededSessionClear = true;
          }
          if (!hasSucceededSessionClear) {
            return;
          }
        }

        const tokenProvider = tokenProviderRef.current;
        const identityToken = tokenProvider
          ? await tokenProvider()
          : await getSalesforceChatToken();

        if (!isMounted) {
          return;
        }

        setSalesforceIdentityToken({
          identityTokenType: 'JWT',
          identityToken,
        });

        // Unblock chat in case a previous attempt had failed clearSession.
        // This makes isChatBlocked self-healing if the hook re-initializes
        // (e.g. enabled toggles false → true) and verification succeeds.
        setSalesforceChatBlocked(false);
        setVerificationStatus('verified');
      } catch (error) {
        if (isMounted) {
          // If clearSession was attempted but didn't succeed, this catch is from
          // the clearSession failure specifically. Hide the widget to prevent the
          // previous user's stale session from being visible, and surface a distinct
          // status so integrators can show an appropriate error (e.g. a toast).
          if (hasAttemptedSessionClear && !hasSucceededSessionClear) {
            setSalesforceChatBlocked(true);
            setVerificationStatus('session-clear-failed');
          } else {
            setVerificationStatus('failed');
          }
        }

        captureError(
          error instanceof Error
            ? error
            : new Error(
                `Failed to obtain or send Salesforce Messaging identity token: ${String(
                  (error as Event)?.type ?? error,
                )}`,
              ),
          {
            errorName: 'Salesforce Messaging Identity Token Failure',
          },
        );
        // eslint-disable-next-line no-console
        console.error(
          '[Salesforce Live Chat] Failed to obtain or send identity token:',
          error,
        );
      }
    };

    const attemptInitialIdentityTokenSend = () => {
      if (!isMounted) {
        return;
      }

      // We only want to send a token once both Salesforce Messaging and Auth are ready.
      if (!messagingReadyForHook || !hasAuthReady) {
        return;
      }

      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      sendIdentityToken({ clearExistingSession: true });
    };

    const unsubscribeReadyListener = onSalesforceEvent(
      SALESFORCE_EVENTS.READY,
      () => {
        messagingReadyForHook = true;
        attemptInitialIdentityTokenSend();
      },
    );

    const unsubscribeExpiredListener = onSalesforceEvent(
      SALESFORCE_EVENTS.IDENTITY_TOKEN_EXPIRED,
      () => {
        // Ignore expiry events if the session was never successfully cleared.
        // This prevents responding to a *stale* user's token expiry (e.g. User A's
        // identity token expires after User B logs in but clearSession failed).
        if (!hasSucceededSessionClear) return;
        // Per Salesforce docs, we must respond within 30 seconds with a new token.
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        sendIdentityToken({ clearExistingSession: false });
      },
    );

    // Handle "late subscriber" case where Messaging was initialized earlier
    // and the global READY flag is already set before this hook mounts.
    if (
      typeof window !== 'undefined' &&
      window.leagueSalesforceMessagingReady
    ) {
      messagingReadyForHook = true;
    }

    // When a custom token provider is supplied, skip the Auth listener entirely;
    // the provider's getToken() is assumed to handle its own readiness (e.g. by
    // returning a promise that waits until a token is available).
    let unsubscribeAuthListener: (() => void) | undefined;
    if (tokenProviderRef.current) {
      hasAuthReady = true;
      attemptInitialIdentityTokenSend();
    } else {
      unsubscribeAuthListener = Auth.addOnAuthListener(() => {
        hasAuthReady = true;
        attemptInitialIdentityTokenSend();
      }, true);
    }

    return () => {
      isMounted = false;
      setSalesforceChatTokenProvider(undefined);
      unsubscribeReadyListener();
      unsubscribeExpiredListener();
      unsubscribeAuthListener?.();
    };
  }, [enabled]);

  return { verificationStatus };
};
